import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    CardContent,
    Card,
    Skeleton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import FormData from 'form-data';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckIcon from '@mui/icons-material/Check';
import { styles } from './styles';
import { Plus } from '../../../assets/icons/plus';
import { Remove } from '../../../assets/icons/remove';
import {
    usePreModifyChangeRequestPolicyMutation,
    useModifyChangeRequestPolicyMutation,
} from '../../../api/changeRequestPolicies';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ModifyCRPolicies = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [isTouched, setTouched] = useState(false);
    const { user } = useSelector((state) => state.session);
    const { enqueueSnackbar } = useSnackbar();
    const [employeeList, setEmployeeList] = useState([]);

    const [preModifyChangeRequestPolicy, { data, isLoading: isPreModifyLoading }] =
        usePreModifyChangeRequestPolicyMutation();
    const [modifyChangeRequestPolicy, { isLoading: isModifyLoading }] = useModifyChangeRequestPolicyMutation();

    const {
        availableStatuses = [],
        changeRequestPolicy = {},
        majorApprovers = [],
        minorApprovers = [],
        clientApprovers = [],
        clientApproverId,
    } = data || {};

    const formik = useFormik({
        initialValues: {
            name: changeRequestPolicy?.name ?? '',
            status: changeRequestPolicy?.status ?? 'Active',
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .max(255)
                .required('Name is required')
                .matches(
                    /^[A-Za-z0-9 :,\-]+$/,
                    'Name can contain only letters, numbers, spaces, commas, colon, and hyphens',
                ),
            status: Yup.string().max(255).required('Status is required'),
        }),
        onSubmit: async (values, helpers) => {
            try {
                handleUpdate(values);
                // helpers.resetForm();
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        const data = new FormData();
        data.append('id', Number(params?.id));
        try {
            preModifyChangeRequestPolicy(data).unwrap();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!data) return;
        let employeeListById = {};
        minorApprovers?.forEach((item) => (employeeListById[item?.id] = item));
        majorApprovers?.forEach((item) => (employeeListById[item?.id] = item));
        clientApprovers?.forEach((item) => (employeeListById[item?.id] = item));

        const rows = [];
        let index = 0;
        (changeRequestPolicy?.approvers ?? []).forEach((item) => {
            const type = item?.categoryType?.name;
            if (type !== 'Minor' && type !== 'Major') return;
            rows.push({
                id: `employeeList_${index++}`,
                label: 'Approver',
                selectValue: employeeListById[item?.employee?.id],
                type,
                isActive: true,
            });
        });

        rows.push({
            id: `employeeList_${index++}`,
            label: 'Approver',
            selectValue: employeeListById[clientApproverId],
            type: 'Client',
            isActive: true,
        });

        setEmployeeList(rows);
    }, [data]);

    const handleRemove = (id, type) => {
        if (!isTouched) setTouched(true);
        const activeList = employeeList.filter((item) => item.isActive && item.type === type);
        if (type === 'Client') return;
        if (activeList.length > 1) {
            setEmployeeList((prev) =>
                prev.map((item) => (item?.id === id && item.type === type ? { ...item, isActive: false } : item)),
            );
        }
    };

    const handleUpdate = async (data) => {
        let majorApproverIds = [];
        let minorApproverIds = [];
        let clientApproverIdsList = [];

        const { name, status } = data;

        const payload = {
            id: params?.id,
            name,
            status,
        };
        employeeList
            .filter((item) => item.isActive)
            .forEach((item) => {
                if (item.selectValue === '') return;
                if (item.type === 'Minor') minorApproverIds.push(item?.selectValue?.id);
                else if (item.type === 'Major') majorApproverIds.push(item?.selectValue?.id);
                else if (item.type === 'Client') clientApproverIdsList.push(item?.selectValue?.id);
            });
        payload['approverCount'] = minorApproverIds.length + majorApproverIds.length + clientApproverIdsList.length;
        payload['minorApproverIds'] = `${minorApproverIds}`;
        payload['majorApproverIds'] = `${majorApproverIds}`;
        payload['clientApproverId'] = clientApproverIdsList.length > 0 ? String(clientApproverIdsList[0]) : '';
        try {
            const response = await modifyChangeRequestPolicy(payload).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
            navigate('/changerequest/crpolicies');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }

        setTouched(false);
    };

    const handleAddEmployeeList = (type) => {
        if (type === 'Client') return;
        setEmployeeList((prev) => {
            let maxIdx = -1;
            prev.forEach((item) => {
                const m = /^employeeList_(\d+)$/.exec(item.id);
                if (m) maxIdx = Math.max(maxIdx, parseInt(m[1], 10));
            });
            const nextId = `employeeList_${maxIdx + 1}`;
            return [
                ...prev,
                {
                    id: nextId,
                    label: 'Approver',
                    selectValue: '',
                    type,
                    isActive: true,
                },
            ];
        });
    };

    const handleChange = (e, id, type) => {
        if (!isTouched) setTouched(true);
        setEmployeeList((prevList) =>
            prevList.map((item) =>
                item?.id === id && item.type === type ? { ...item, selectValue: e.target.value } : item,
            ),
        );
    };

    const handleBack = () => {
        navigate(-1);
    };

    const isDisabled = useMemo(() => {
        const activeMajorList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Major' && item.selectValue !== '',
        );
        const activeMinorList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Minor' && item.selectValue !== '',
        );
        const activeClientList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Client' && item.selectValue !== '',
        );
        return (
            activeMajorList?.length === 0 ||
            activeMinorList?.length === 0 ||
            (user.isClientHqEnabled && activeClientList?.length === 0)
        );
    }, [employeeList]);

    const { majorApproverList, minorApproverList, clientApproverList } = useMemo(() => {
        const minorEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Minor' && item.isActive && item.selectValue !== '')
            .map((item) => parseInt(item.selectValue?.id));
        const majorEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Major' && item.isActive && item.selectValue !== '')
            .map((item) => parseInt(item.selectValue?.id));
        const clientEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Client' && item.isActive && item.selectValue !== '')
            .map((item) => parseInt(item.selectValue?.id));
        const minorApproverList =
            minorApprovers?.filter((item) => minorEmployeeSelectedList?.indexOf(item?.id) < 0) || [];
        const majorApproverList =
            majorApprovers?.filter((item) => majorEmployeeSelectedList?.indexOf(item?.id) < 0) || [];
        const clientApproverList =
            clientApprovers?.filter((item) => clientEmployeeSelectedList?.indexOf(item?.id) < 0) || [];
        return { majorApproverList, minorApproverList, clientApproverList };
    }, [majorApprovers, minorApprovers, clientApprovers, employeeList]);

    const { minorEmployeeList, majorEmployeeList, clientEmployeeList } = useMemo(() => {
        const minorEmployeeList = employeeList?.filter((item) => item.type === 'Minor' && item.isActive);
        const majorEmployeeList = employeeList?.filter((item) => item.type === 'Major' && item.isActive);
        const clientEmployeeList = employeeList?.filter((item) => item.type === 'Client' && item.isActive);
        return { minorEmployeeList, majorEmployeeList, clientEmployeeList };
    }, [employeeList]);

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Edit Change Policy</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid
                                item
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <Button
                                    sx={styles.backButton}
                                    startIcon={<ChevronLeftIcon fontSize="small" />}
                                    onClick={handleBack}
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                        {isPreModifyLoading ? (
                            <Box
                                sx={{
                                    py: 4,
                                }}
                            >
                                <Skeleton height={42} />
                                <Skeleton />
                                <Skeleton />
                            </Box>
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={styles.basicInformation} component="form" noValidate autoComplete="off">
                                    <Grid container columnSpacing={6} rowSpacing={3}>
                                        <Grid item xs={6}>
                                            <TextField
                                                error={Boolean(formik.touched.name && formik.errors.name)}
                                                fullWidth
                                                helperText={formik.touched.name && formik.errors.name}
                                                required
                                                id="name"
                                                label="Name"
                                                name="name"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                value={formik.values.name}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(formik.touched.status && formik.errors.status)}
                                                label="Available Status"
                                                name={'status'}
                                                id="status"
                                                size="small"
                                                helperText={formik.touched.status && formik.errors.status}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                select
                                                children={availableStatuses.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                                value={formik.values.status}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box sx={styles.bottomBLine}></Box>
                                <Box sx={styles.additionalPreOfferButton}>
                                    <Typography variant="h6" sx={styles.basicInformationText}>
                                        Minor change request approvers
                                    </Typography>
                                    <Button
                                        color="primary"
                                        onClick={() => handleAddEmployeeList('Minor')}
                                        size="large"
                                        variant="text"
                                        startIcon={<Plus />}
                                    >
                                        Add Approver
                                    </Button>
                                </Box>
                                <Box sx={styles.employeeListsBox}>
                                    {minorEmployeeList?.map((item) => (
                                        <Box key={item?.id} sx={styles.inputsAdditionalPreOfferBox}>
                                            <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                <InputLabel>{item.label}</InputLabel>
                                                <Select
                                                    label={item.label}
                                                    value={item.selectValue}
                                                    onChange={(e) => handleChange(e, item?.id, 'Minor')}
                                                >
                                                    {(item.selectValue === ''
                                                        ? minorApproverList
                                                        : [item.selectValue, ...minorApproverList]
                                                    )?.map((val) => (
                                                        <MenuItem value={val} key={val?.id}>
                                                            {val?.fullNameWithRole}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {minorEmployeeList.length > 1 && (
                                                <Box sx={styles.removeButtonBox}>
                                                    <Button
                                                        onClick={() => handleRemove(item?.id, 'Minor')}
                                                        size="large"
                                                        sx={styles.removeButton}
                                                        variant="text"
                                                        startIcon={<Remove />}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={styles.bottomBLine}></Box>
                                <Box sx={styles.additionalPreOfferButton}>
                                    <Typography variant="h6" sx={styles.basicInformationText}>
                                        Major change request approvers
                                    </Typography>
                                    <Button
                                        color="primary"
                                        onClick={() => handleAddEmployeeList('Major')}
                                        size="large"
                                        variant="text"
                                        startIcon={<Plus />}
                                    >
                                        Add Approver
                                    </Button>
                                </Box>
                                <Box sx={styles.employeeListsBox}>
                                    {majorEmployeeList?.map((item) => (
                                        <Box key={item?.id} sx={styles.inputsAdditionalPreOfferBox}>
                                            <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                <InputLabel>{item.label}</InputLabel>
                                                <Select
                                                    label={item.label}
                                                    value={item.selectValue}
                                                    onChange={(e) => handleChange(e, item?.id, 'Major')}
                                                >
                                                    {(item.selectValue === ''
                                                        ? majorApproverList
                                                        : [item.selectValue, ...majorApproverList]
                                                    )?.map((val) => (
                                                        <MenuItem value={val} key={val?.id}>
                                                            {val?.fullNameWithRole}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            {majorEmployeeList.length > 1 && (
                                                <Box sx={styles.removeButtonBox}>
                                                    <Button
                                                        onClick={() => handleRemove(item?.id, 'Major')}
                                                        size="large"
                                                        sx={styles.removeButton}
                                                        variant="text"
                                                        startIcon={<Remove />}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                                {user.isClientHqEnabled && (
                                    <React.Fragment>
                                        <Box sx={styles.bottomBLine}></Box>
                                        <Box sx={styles.additionalPreOfferButton}>
                                            <Typography variant="h6" sx={styles.basicInformationText}>
                                                Client change request approver
                                            </Typography>
                                        </Box>
                                        <Box sx={styles.employeeListsBox}>
                                            {clientEmployeeList?.map((item) => (
                                                <Box key={item?.id} sx={styles.inputsAdditionalPreOfferBox}>
                                                    <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                        <InputLabel>{item.label}</InputLabel>
                                                        <Select
                                                            label={item.label}
                                                            value={item.selectValue}
                                                            onChange={(e) => handleChange(e, item?.id, 'Client')}
                                                        >
                                                            {(item.selectValue === ''
                                                                ? clientApproverList
                                                                : [item.selectValue, ...clientApproverList]
                                                            )?.map((val) => (
                                                                <MenuItem value={val} key={val?.id}>
                                                                    {val?.fullNameWithRole}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            ))}
                                        </Box>
                                    </React.Fragment>
                                )}
                                <Grid
                                    item
                                    sx={{
                                        pt: 2,
                                    }}
                                    xs={12}
                                >
                                    <Box>
                                        <Box sx={styles.bottomBLine}></Box>
                                        <Box sx={styles.createButtonBox}>
                                            <Button
                                                disabled={
                                                    (!formik.dirty && !isTouched) ||
                                                    (formik.values.status === 'Active' && isDisabled) ||
                                                    isModifyLoading
                                                }
                                                color="primary"
                                                onClick={formik.handleSubmit}
                                                size="large"
                                                sx={styles.createButton}
                                                variant="contained"
                                                startIcon={<CheckIcon fontSize="small" />}
                                            >
                                                Update
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ModifyCRPolicies;
